import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Card, Button, Row, Col } from 'antd'
import type { IBasePropsState as TemplatesConfigurationProps } from '@src/slices/base-props.slice'
import type { RootState } from '@src/store'
import { Paths } from '@src/enums/paths'
import { clearBaseProps, setBaseProps } from '@src/slices/base-props.slice'
import NewTemplate from '@src/modules/organisms/new-template'
import EditTemplate from '@src/modules/organisms/edit-template'
import { activateVariables, clearTemplateStore, redirectInit } from '@src/slices/configuration.slice'
import Main from './main'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { show } from '@src/slices/configuration.slice'
import styles from './TemplatesConfiguration.module.scss'
import ContainerVariables from '@src/modules/organisms/variables'

const { main, newTemplate, editTemplate } = Paths

export const TemplatesConfiguration: React.FC<TemplatesConfigurationProps> = ({
  context,
  baseUrl,
  resource,
  nodes,
  actionsCallback,
  overwriteColumns,
  permissions,
  isSignatureFlow,
  biometric,
  documentType,
  tags,
  url,
  t,
  handleReturn,
  filterWorkspaceCallback,
  onDuplicate
}) => {
  const dispatch = useDispatch()
  const { path } = useSelector(({ configuration }: RootState) => configuration)
  const showVariables = useSelector(show)
  const [textSearch, setTextSearch] = useState<string>("")
  const { baseUrl: baseUrlState, context: contextState } = useSelector(
    ({ baseProps }: RootState) => baseProps
  )

  const handleSearch = () => {
    setTextSearch('')
  }

  const goBack = () => {
    if(showVariables) {
      dispatch(activateVariables({ visibility: false }))
    }
    if (path === main) {
      return
    }
    handleSearch()
    handleReturn?.()
    dispatch(clearTemplateStore({ isNew: path === newTemplate }))
    dispatch(redirectInit())
  }

  useEffect(() => {
    dispatch(setBaseProps({ context, baseUrl, resource }))

    return () => {
      handleReturn?.()
      dispatch(clearBaseProps())
      dispatch(redirectInit())
      dispatch(clearTemplateStore({ isNew: true }))
      dispatch(clearTemplateStore({ isNew: false }))
      dispatch(activateVariables({ visibility: false }))
      setTextSearch('')
    }
  }, [resource])

  const variableVisibility = () => {
    dispatch(activateVariables({visibility: !showVariables}))
  }

  useEffect(() => {
    actionsCallback?.onPathChange && actionsCallback?.onPathChange(path)
  }, [path])

  const basePropsResolved = useMemo(
    () => baseUrlState && contextState,
    [baseUrlState, contextState]
  )

  return (
    <Card bordered={false} className={styles.card}>
      <Row className={styles.rowTransition} gutter={[16, 8]}>
        <Col className={ styles.border } span={showVariables ? 18 : 24} id='card'>
          {path !== main && (
            <React.Fragment>
              <Button onClick={goBack} className={styles.btnBack}>
                <FontAwesomeIcon icon={faArrowLeft as IconProp} />
              </Button>
              <p className={styles.textBreadcrumb}>
                {path === newTemplate ? 'Nueva plantilla' : 'Editar plantilla'}
              </p>
            </React.Fragment>
          )}
          {basePropsResolved && path === main && (
            <Main
              onEditTemplate={actionsCallback?.onEditTemplate}
              overwriteColumns={overwriteColumns}
              permissions={permissions}
              url={url}
              onDuplicate={onDuplicate}
            />
          )}
          {basePropsResolved && path === newTemplate && (
            <NewTemplate
              templateHeaderNode={nodes?.newTemplateHeaderNode}
              actionsCallback={actionsCallback}
              isSignatureFlow={isSignatureFlow}
              biometric={biometric}
              documentType={documentType}
              tags={tags}
              t={t}
              handleReturn={handleReturn}
              variableVisibility={variableVisibility}
              handleSearch={handleSearch}
              filterWorkspaceCallback={filterWorkspaceCallback}
            />
          )}
          {basePropsResolved && path === editTemplate && (
            <EditTemplate
              templateHeaderNode={nodes?.editTemplateHeaderNode}
              actionsCallback={actionsCallback}
              isSignatureFlow={isSignatureFlow}
              biometric={biometric}
              documentType={documentType}
              tags={tags}
              t={t}
              variableVisibility={variableVisibility}
              handleReturn={handleReturn}
              handleSearch={handleSearch}
              filterWorkspaceCallback={filterWorkspaceCallback}
            />
          )}
        </Col >
        <Col span={showVariables ? 6 : 0}>
          <ContainerVariables setTextSearch={setTextSearch} textSearch={textSearch}/>
        </Col>
      </Row>
    </Card>
  )
}
